import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarDays,
  Wallet,
  Users,
  ArrowUpRight,
  ClipboardList,
  MessageCircle,
  CheckCircle2,
  Bell,
} from "lucide-react";
import { Link } from "react-router-dom";
import SessionTracker from "@/components/SessionTracker";
import { buildWhatsAppLink } from "@/lib/whatsapp";
import { useConfig } from "@/context/ConfigContext";

const fmtGs = (n) =>
  new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG", maximumFractionDigits: 0 }).format(n || 0);

const today = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const { user } = useAuth();
  const cfg = useConfig();
  const [stats, setStats] = useState(null);
  const [panel, setPanel] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [pacientes, setPacientes] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, pa, al, t, p] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/panel"),
          api.get("/alertas"),
          api.get("/turnos", { params: { fecha_desde: today(), fecha_hasta: today() } }),
          api.get("/pacientes"),
        ]);
        setStats(s.data);
        setPanel(pa.data);
        setAlertas(al.data);
        setTurnos(t.data);
        setPacientes(p.data);
      } catch (err) {
        console.warn("No se pudo cargar el dashboard:", err);
      }
    })();
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Buenos días";
    if (h < 20) return "Buenas tardes";
    return "Buenas noches";
  };

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1400px]">
      <PageHeader
        eyebrow={new Date().toLocaleDateString("es-PY", { weekday: "long", day: "numeric", month: "long" })}
        title={`${greeting()}, ${user?.name?.split(" ")[0] || ""}`}
        description="Un vistazo a la actividad del consultorio de hoy."
      />

      {/* Panel diario */}
      {panel && (
        <div className="mb-6 rounded-2xl border border-neutral-200 bg-gradient-to-br from-[hsl(var(--sage-light))]/60 to-white p-6 md:p-8" data-testid="panel-diario">
          <div className="overline mb-2">Panel diario</div>
          <p className="text-lg md:text-xl font-semibold text-neutral-900 leading-relaxed">
            Hoy tenés{" "}
            <b>{panel.hoy.total}</b> {panel.hoy.total === 1 ? cfg.termino_sesion || "consulta" : (cfg.termino_paciente_plural ? "consultas" : "consultas")} agendadas
            {panel.hoy.confirmadas > 0 && <> · <b className="text-[hsl(var(--sage-hover))]">{panel.hoy.confirmadas} confirmadas</b></>}
            {panel.hoy.pendientes_confirmar > 0 && <> · <b className="text-amber-700">{panel.hoy.pendientes_confirmar} sin confirmar</b></>}
            {panel.hoy.canceladas > 0 && <> · <b className="text-[hsl(var(--terracotta))]">{panel.hoy.canceladas} canceladas</b></>}
            {panel.seguimientos_pendientes > 0 && <> · <b className="text-blue-700">{panel.seguimientos_pendientes} seguimientos pendientes</b></>}
            {panel.manana > 0 && <> · <span className="text-neutral-600">mañana {panel.manana}</span></>}
            .
          </p>
        </div>
      )}

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-5">
        <StatCard
          testId="stat-turnos-hoy"
          label="Turnos hoy"
          value={stats?.turnos_hoy ?? "—"}
          icon={CalendarDays}
          accent="sage"
          to="/agenda"
        />
        <StatCard
          testId="stat-pacientes-activos"
          label="Pacientes activos"
          value={stats?.pacientes_activos ?? "—"}
          icon={Users}
          accent="neutral"
          to="/pacientes"
        />
        <StatCard
          testId="stat-pagos-pendientes"
          label="Pagos pendientes"
          value={fmtGs(stats?.pagos_pendientes_monto)}
          sub={`${stats?.pagos_pendientes_count ?? 0} pacientes`}
          icon={Wallet}
          accent="terracotta"
          to="/pagos"
        />
        <StatCard
          testId="stat-caja-hoy"
          label="Caja del día"
          value={fmtGs(stats?.caja_hoy_saldo)}
          sub={`+${fmtGs(stats?.caja_hoy_ingresos)} · -${fmtGs(stats?.caja_hoy_egresos)}`}
          icon={ClipboardList}
          accent="sage"
          to="/caja"
        />

        {/* Alertas */}
        {alertas.length > 0 && (
          <div className="card-clinic p-6 md:p-8 col-span-1 md:col-span-3 lg:col-span-4" data-testid="alertas-card">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Bell className="w-4 h-4 text-amber-700" />
              </div>
              <h3 className="text-lg font-bold tracking-tight">Alertas del día</h3>
              <span className="text-xs text-neutral-500">({alertas.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {alertas.slice(0, 6).map((a) => (
                <Link
                  key={a.id}
                  to={a.paciente_id ? `/pacientes/${a.paciente_id}` : "/agenda"}
                  data-testid={`alerta-${a.id}`}
                  className={`block rounded-lg border p-3 text-sm transition-colors hover:shadow-sm ${
                    a.severidad === "warning"
                      ? "border-amber-200 bg-amber-50/50 hover:bg-amber-50"
                      : "border-blue-200 bg-blue-50/50 hover:bg-blue-50"
                  }`}
                >
                  <div className="font-semibold text-neutral-900">{a.titulo}</div>
                  <div className="text-xs text-neutral-600 mt-0.5">{a.mensaje}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Turnos de hoy - large card */}
        <div className="card-clinic p-6 md:p-8 col-span-1 md:col-span-2 lg:col-span-3" data-testid="turnos-hoy-card">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="overline mb-1">Agenda</div>
              <h3 className="text-xl font-bold tracking-tight">Turnos de hoy · Recordatorios</h3>
              <p className="text-xs text-neutral-500 mt-1">
                Enviá el mensaje de confirmación por WhatsApp con un click.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {turnos.filter((t) => t.paciente_id && t.paciente_telefono && !t.confirmado_por_paciente).length > 0 && (
                <span className="text-xs text-neutral-500">
                  {turnos.filter((t) => t.paciente_id && t.paciente_telefono && !t.confirmado_por_paciente).length} pendientes
                </span>
              )}
              <Link to="/agenda" className="btn-ghost-pill text-sm">
                Ver agenda <ArrowUpRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
          {turnos.length === 0 ? (
            <EmptyState text="No hay turnos programados para hoy." />
          ) : (
            <div className="space-y-2">
              {turnos.slice(0, 12).map((t) => {
                const canWhatsApp = !!t.paciente_id;
                const noPhone = canWhatsApp && !t.paciente_telefono;
                return (
                  <div
                    key={t.id}
                    className="flex items-center gap-4 py-2.5 px-3 rounded-lg row-hover border border-transparent hover:border-neutral-200"
                    data-testid={`dashboard-turno-${t.id}`}
                  >
                    <span className="font-mono text-sm font-semibold text-neutral-800 w-16">{t.hora}</span>
                    <span
                      className="w-1.5 h-8 rounded-full"
                      style={{ backgroundColor: t.profesional_color || "#6B8E6B" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-neutral-900 truncate flex items-center gap-1.5">
                        {t.paciente_nombre}
                        {t.confirmado_por_paciente && (
                          <CheckCircle2
                            className="w-3.5 h-3.5 text-[hsl(var(--sage-hover))]"
                            aria-label="Confirmado"
                          />
                        )}
                      </div>
                      <div className="text-xs text-neutral-500 truncate">
                        {t.profesional_nombre} · {({origen:"Test ORIGEN",sesion:"Sesión",reunion:"Reunión",consulta_externa:"Consulta externa",otro:"Otro"})[t.tipo] || t.tipo}
                        {noPhone && <span className="text-[hsl(var(--terracotta))] ml-1">· sin teléfono</span>}
                      </div>
                    </div>
                    <span
                      className={`hidden sm:inline text-xs font-medium px-2.5 py-1 rounded-full ${
                        t.estado === "asistio"
                          ? "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]"
                          : t.estado === "cancelado"
                          ? "bg-neutral-100 text-neutral-500"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {t.estado}
                    </span>
                    {canWhatsApp && (
                      <a
                        href={buildWhatsAppLink(t, cfg)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`whatsapp-dashboard-${t.id}`}
                        title={noPhone ? "Enviar sin destinatario (elegir contacto)" : "Enviar confirmación por WhatsApp"}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors shrink-0 ${
                          t.confirmado_por_paciente
                            ? "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                            : "bg-[#25D366] text-white hover:bg-[#1FB855]"
                        }`}
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span className="hidden md:inline">
                          {t.confirmado_por_paciente ? "Reenviar" : "WhatsApp"}
                        </span>
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Progreso pacientes */}
        <div className="card-clinic p-6 md:p-8 col-span-1 lg:row-span-1" data-testid="progreso-card">
          <div className="mb-4">
            <div className="overline mb-1">Tratamientos</div>
            <h3 className="text-xl font-bold tracking-tight">En progreso</h3>
          </div>
          {pacientes.filter((p) => p.estado === "activo").length === 0 ? (
            <EmptyState text="Aún no hay pacientes activos." />
          ) : (
            <div className="space-y-4">
              {pacientes
                .filter((p) => p.estado === "activo")
                .slice(0, 6)
                .map((p) => (
                  <Link
                    key={p.id}
                    to={`/pacientes/${p.id}`}
                    className="block group"
                    data-testid={`progreso-paciente-${p.id}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-semibold text-neutral-900 group-hover:text-[hsl(var(--sage-hover))]">
                        {p.nombre} {p.apellido}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {p.sesiones_completadas}/{p.sesiones_totales}
                      </span>
                    </div>
                    <SessionTracker completed={p.sesiones_completadas} total={p.sesiones_totales} />
                  </Link>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent = "sage", to, testId }) {
  const accents = {
    sage: "bg-[hsl(var(--sage-light))] text-[hsl(var(--sage-hover))]",
    terracotta: "bg-[hsl(var(--terracotta))]/10 text-[hsl(var(--terracotta-hover))]",
    neutral: "bg-neutral-100 text-neutral-700",
  };
  const Inner = (
    <div className="card-clinic p-6 md:p-7 h-full" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
        {to && <ArrowUpRight className="w-4 h-4 text-neutral-400" />}
      </div>
      <div className="mt-6">
        <div className="overline mb-1">{label}</div>
        <div className="text-3xl font-bold tracking-tight text-neutral-900">{value}</div>
        {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to}>{Inner}</Link> : Inner;
}

function EmptyState({ text }) {
  return (
    <div className="text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-xl px-5 py-8 text-center">
      {text}
    </div>
  );
}
